/**
 * Host-aware ClinicalHUD marketing routing.
 *
 * On clinicalhud.com the splash is `/`; the hub is `/overview`, with
 * /how-it-works, /ddx-vs-pearls, and /request-access beside it.
 * On app domains the same pages live under /clinicalhud so they do not collide
 * with other product routes.
 *
 * localStorage CLINICALHUD_MARKETING_HOST=1 previews dedicated-host routing on any host.
 */

export const OVERVIEW_SLUG = 'overview';
export const MARKETING_SLUGS = [
  OVERVIEW_SLUG,
  'how-it-works',
  'ddx-vs-pearls',
  'request-access',
];

/** Authenticated product surfaces — keep in sync with AppRoutes. */
export const CLINICALHUD_PRODUCT_PATHS = {
  hud: '/clinical-hud',
  hud2: '/clinical-hud-2',
  hud3: '/clinical-hud-3',
  hud4: '/clinical-hud-4',
};

const MARKETING_BASE = '/clinicalhud';
const HOST_PREVIEW_KEY = 'CLINICALHUD_MARKETING_HOST';

export function isClinicalHudMarketingHost() {
  if (typeof window === 'undefined') return false;
  try {
    if (window.localStorage?.getItem(HOST_PREVIEW_KEY) === '1') return true;
  } catch {
    // localStorage may be unavailable (private mode).
  }
  const host = window.location.hostname.toLowerCase();
  return (
    host === 'clinicalhud.com' ||
    host.endsWith('.clinicalhud.com') ||
    host === 'clinicalhud-com.web.app' ||
    host === 'clinicalhud-com.firebaseapp.com'
  );
}

/** Marketing root — splash. */
export function marketingHubPath() {
  return isClinicalHudMarketingHost() ? '/' : MARKETING_BASE;
}

/** Hub page (not the splash). */
export function marketingOverviewPath() {
  return marketingPagePath(OVERVIEW_SLUG);
}

export function marketingPagePath(slug) {
  if (!slug) return marketingHubPath();
  return isClinicalHudMarketingHost() ? `/${slug}` : `${MARKETING_BASE}/${slug}`;
}

/**
 * Bridge into the main-app sign-in flow, optionally landing on ClinicalHUD2
 * after auth (mirrors ASI authPath({ next })).
 */
export function authPath({ next } = {}) {
  const base = '/auth';
  if (!next) return base;
  return `${base}?next=${encodeURIComponent(next)}`;
}

/** True when pathname is a ClinicalHUD marketing surface on any host. */
export function isClinicalHudMarketingPath(pathname = '') {
  if (typeof pathname !== 'string') return false;
  const normalized = pathname.replace(/\/+$/, '') || '/';
  if (isClinicalHudMarketingHost()) {
    if (normalized === '/') return true;
    const segment = normalized.replace(/^\/+/, '').split('/')[0];
    return MARKETING_SLUGS.includes(segment);
  }
  return (
    normalized === MARKETING_BASE ||
    normalized.startsWith(`${MARKETING_BASE}/`)
  );
}

/** Grant-gated product app (not marketing). */
export function isClinicalHudProductPath(pathname = '') {
  if (typeof pathname !== 'string') return false;
  const normalized = pathname.replace(/\/+$/, '') || '/';
  return normalized === '/clinical-hud' || normalized.startsWith('/clinical-hud/');
}

/** ClinicalHUD2 workspace / list. */
export function isClinicalHud2ProductPath(pathname = '') {
  if (typeof pathname !== 'string') return false;
  const normalized = pathname.replace(/\/+$/, '') || '/';
  return (
    normalized === '/clinical-hud-2' || normalized.startsWith('/clinical-hud-2/')
  );
}

/** ChartMind-7 workspace / list. Does not match ClinicalHUD2. */
export function isChartMind7ProductPath(pathname = '') {
  if (typeof pathname !== 'string') return false;
  const normalized = pathname.replace(/\/+$/, '') || '/';
  return normalized === '/chartmind-7' || normalized.startsWith('/chartmind-7/');
}

/** Settings gear: refresh interval, ChartMind DDx engine, Even G2 preview. */
export function showsClinicalHudRefreshSettings(pathname = '') {
  return (
    isClinicalHudProductPath(pathname) ||
    isClinicalHud2ProductPath(pathname) ||
    isChartMind7ProductPath(pathname)
  );
}

/** Global top-bar wordmark: dedicated host, marketing paths, or product app. */
export function showsClinicalHudTopBarWordmark(pathname = '') {
  return (
    isClinicalHudMarketingHost() ||
    isClinicalHudMarketingPath(pathname) ||
    isClinicalHudProductPath(pathname)
  );
}
